import React, { useState, useMemo, useEffect, useContext } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { ConfigContext } from "../context/GlobalContext";
import { EditExpense } from "../data";
import Currancy from "../Utils/currency.json";
import CountryFlags from "../Utils/countries.json";
import { getImage } from "../Utils/image_helper";

const ExpenseCurrencyMigrationModal = ({ show, expenses = [], businessCurrency = "INR", onClose }) => {
  const { state, dispatch } = useContext(ConfigContext);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [saving, setSaving] = useState(false);

  const currencyOptions = useMemo(() => {
    return Currancy.CountryCurrencyResponse.map(row => {
      const country = CountryFlags?.CurrencyResponse?.find(c => c.isoAlpha2 === row.countryCode);
      return {
        value: row.countryCode,
        label: `${row.name} (${row.currencyCode} - ${row.currencytSymbol})`,
        flag: country?.flag,
        raw: row
      };
    });
  }, []);

  const formatOptionLabel = ({ flag, raw }, { context }) => (
    <div className="d-flex align-items-center gap-2">
      <img
        src={flag ? `data:image/png;base64,${flag}` : getImage("euro_flag")}
        alt="flag"
        width="20"
        height="12"
        style={{ objectFit: "cover", borderRadius: 1 }}
      />
      <span style={{ fontSize: "13px" }}>
        {context === "value"
          ? `${raw.currencyCode} - ${raw.currencytSymbol}`
          : `${raw.name} (${raw.currencyCode} - ${raw.currencytSymbol})`
        }
      </span>
    </div>
  );

  const defaultCountryCode = useMemo(() => {
    const matched = Currancy.CountryCurrencyResponse.find(row => row.currencyCode === businessCurrency);
    return matched ? matched.countryCode : "IN";
  }, [businessCurrency]);

  useEffect(() => {
    if (show) {
      setSelectedCountry(defaultCountryCode);
    }
  }, [show, defaultCountryCode]);

  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption?.value || "");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const matchedOption = currencyOptions.find(opt => opt.value === selectedCountry);
      if (!matchedOption) {
        setSaving(false);
        return;
      }

      if (expenses.length > 0) {
        const selectedBusiness = state?.BUSINESSES?.find(b => String(b.companyId) === String(expenses[0].companyId));
        const targetUid = selectedBusiness?.isShared ? selectedBusiness.ownerUid : state?.USERID;

        for (const exp of expenses) {
          const payload = {
            ...exp,
            currency: matchedOption.raw.currencyCode,
            updatedAt: Date.now().toString(),
          };
          await EditExpense(state, dispatch, exp.uniqueID, payload, targetUid);
        }
      }

      toast.success("Expense currencies updated successfully!");
      if (onClose) onClose();
    } catch (error) {
      console.error("Error saving expense currencies:", error);
      toast.error("Failed to update some records. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "var(--neutral-50)",
      borderColor: "var(--border-color)",
      color: "var(--text-secondary-light)",
      boxShadow: "none",
      minHeight: "38px",
      height: "38px",
      borderRadius: "8px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 10px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "36px",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--neutral-50)",
      border: "1px solid var(--border-color)",
      zIndex: 9999,
      width: "max-content",
      minWidth: "100%",
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--text-secondary-light)",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--text-secondary-light)",
    }),
    input: (base) => ({
      ...base,
      color: "var(--text-secondary-light)",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused
        ? "var(--neutral-200)"
        : "transparent",
      color: "var(--text-secondary-light)",
      cursor: "pointer",
    }),
  };

  if (!show || expenses.length === 0) return null;

  const businessCompanyId = expenses[0]?.companyId;
  const selectedBusiness = state?.BUSINESSES?.find(
    b => String(b.companyId) === String(businessCompanyId)
  );
  const businessName = selectedBusiness?.companyName || null;

  const currentSelectValue = currencyOptions.find(opt => opt.value === selectedCountry);

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>

      <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1060, overflowY: "auto" }}>
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content shadow border-0 rounded-4" style={{ overflow: "visible" }}>

            <div className="modal-header d-flex flex-column align-items-start gap-1">
              <div className="d-flex align-items-center justify-content-between w-100">
                <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0">
                  <i className="bi bi-currency-exchange text-primary fs-4"></i>
                  Set Expense Currency
                </h5>
              </div>
              {businessName && (
                <div className="d-flex align-items-center gap-2 mt-1">
                  <span className="text-secondary-light fs-13 fw-medium">Business:</span>
                  <span
                    className="badge rounded-pill px-12 py-6 fw-semibold fs-12 d-flex align-items-center gap-1"
                    style={{
                      backgroundColor: "var(--primary-50, #eff4ff)",
                      color: "var(--primary-600, #487fff)",
                      border: "1px solid var(--primary-100, #c9d9ff)"
                    }}
                  >
                    <i className="bi bi-building" style={{ fontSize: "11px" }}></i>
                    {businessName}
                  </span>
                </div>
              )}
            </div>

            <div className="modal-body px-24 py-20" style={{ overflow: "visible" }}>
              <p className="text-secondary-light mb-4 fs-14 fw-medium bg-light p-3 rounded border border-neutral-200 text-center">
                Please choose a currency for your expenses. It will be applied to all your existing expenses and can be changed later from the Expense List.
              </p>

              <div className="d-flex flex-column gap-2 mb-3 px-2">
                <label className="fw-semibold text-secondary-light fs-14 mb-0">
                  Select Currency:
                </label>
                <Select
                  options={currencyOptions}
                  value={currentSelectValue}
                  onChange={handleCountryChange}
                  formatOptionLabel={formatOptionLabel}
                  styles={customSelectStyles}
                  placeholder="Select"
                  isDisabled={saving}
                  isSearchable={true}
                />
              </div>
            </div>

            <div className="modal-footer border-0 px-24 py-10 bg-light">
              <button
                type="button"
                className="btn btn-primary fs-13 py-8 px-20 fw-semibold d-flex align-items-center gap-2 animate-all"
                onClick={handleSave}
                disabled={saving || !selectedCountry}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Saving Changes...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExpenseCurrencyMigrationModal;
